import { useAuth } from '@clerk/react'
import type { ReactNode } from 'react'
import {
  createContext,
  use,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useUserSettings } from '@/components/user-settings-context'
import type { TimeWindow } from '@/db/schema/sessions'
import { trpc } from '@/lib/trpc'
import type { UserSettings } from '@/lib/user-settings'
import { useProblemOptional } from '../problem-context'
import { useSessionOptional } from '../session-context'
import { SessionTransitionDialog } from '../session-transition-dialog'
import { type AlarmSound, TimerAlarm } from './timer-alarm'
import {
  clearTimerState,
  loadTimerState,
  type PersistedTimerState,
  reconstructTime,
  saveTimerState,
} from './timer-persistence'

// =============================================================================
// Types
// =============================================================================

interface StopwatchState {
  status: 'idle' | 'running' | 'paused'
  elapsedMs: number
}

interface TimerState {
  status: 'idle' | 'running' | 'paused' | 'overage'
  durationMs: number
  remainingMs: number
}

export interface TimerContextValue {
  activeView: 'stopwatch' | 'timer'
  stopwatch: StopwatchState
  timer: TimerState
  hasTimer: boolean
  startStopwatch: () => void
  pauseStopwatch: () => void
  resetStopwatch: () => void
  startTimer: (durationMs: number) => void
  pauseTimer: () => void
  resetTimer: () => void
  endTimer: () => void
  setActiveView: (view: 'stopwatch' | 'timer') => void
  settings: UserSettings['timer']
  onEditorChange: (charsTyped: number) => void
  saveTimerStateToDb: () => void
  onSessionComplete: () => void
  showTransitionDialog: boolean
  handleNewSessionInteraction: () => boolean
  handleTransitionStartFresh: () => void
  handleTransitionContinue: () => void
  handleTransitionDismiss: () => void
  /**
   * Reset stopwatch + timer to idle/0 and clear time-window state without
   * starting anything. Used when a session has been auto-expired and the
   * user is being routed to a fresh attempt — the next user interaction
   * will start the stopwatch from zero.
   */
  resetForNewAttempt: () => void
}

// =============================================================================
// Context
// =============================================================================

const TimerContext = createContext<TimerContextValue | null>(null)

const TICK_INTERVAL_MS = 100
const PERSIST_DEBOUNCE_MS = 1000

// =============================================================================
// Internal Ref Types
// =============================================================================

interface StopwatchRef {
  status: 'idle' | 'running' | 'paused'
  elapsedMs: number
  lastTickAt: number | null
}

interface TimerRef {
  status: 'idle' | 'running' | 'paused' | 'overage'
  durationMs: number
  remainingMs: number
  lastTickAt: number | null
}

// =============================================================================
// Provider
// =============================================================================

export function TimerProvider({ children }: { children: ReactNode }) {
  const problemCtx = useProblemOptional()
  const slug = problemCtx?.problemSlug ?? null
  const sessionCtx = useSessionOptional()
  const sessionId = sessionCtx?.session?.id ?? null
  const landingMode = sessionCtx?.landingMode ?? null

  const { settings: userSettings, updateSettings: updateUserSettings } =
    useUserSettings()
  const timerSettings = userSettings.timer
  const { isSignedIn } = useAuth()

  // ---------------------------------------------------------------------------
  // React state (triggers re-renders)
  // ---------------------------------------------------------------------------
  const [stopwatch, setStopwatch] = useState<StopwatchState>({
    status: 'idle',
    elapsedMs: 0,
  })
  const [timer, setTimer] = useState<TimerState>({
    status: 'idle',
    durationMs: 0,
    remainingMs: 0,
  })
  const [activeView, setActiveViewState] = useState<'stopwatch' | 'timer'>(
    'stopwatch',
  )
  const [hasTimer, setHasTimerState] = useState(false)

  // ---------------------------------------------------------------------------
  // Refs (mutable, no stale closures in intervals/callbacks)
  // ---------------------------------------------------------------------------
  const stopwatchRef = useRef<StopwatchRef>({
    status: 'idle',
    elapsedMs: 0,
    lastTickAt: null,
  })
  const timerRef = useRef<TimerRef>({
    status: 'idle',
    durationMs: 0,
    remainingMs: 0,
    lastTickAt: null,
  })
  const activeViewRef = useRef<'stopwatch' | 'timer'>('stopwatch')
  const hasTimerRef = useRef(false)
  const settingsRef = useRef(timerSettings)
  const userSettingsRef = useRef(userSettings)
  const alarmRef = useRef(new TimerAlarm())
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const persistTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // ---------------------------------------------------------------------------
  // Session tracking refs — kept in sync with SessionProvider via effects below
  // ---------------------------------------------------------------------------
  const sessionActiveRef = useRef(false)
  const sessionLoadingRef = useRef(true) // default true — assume loading until told otherwise

  // Sync sessionActiveRef from SessionProvider (TimerProvider is a descendant
  // of SessionProvider, so it reads session state directly rather than relying
  // on a callback from SessionProvider which can't reach up the tree).
  useEffect(() => {
    sessionActiveRef.current = !!sessionCtx?.session
  }, [sessionCtx?.session])

  useEffect(() => {
    sessionLoadingRef.current = sessionCtx?.loading ?? false
  }, [sessionCtx?.loading])

  // ---------------------------------------------------------------------------
  // Session transition state
  // ---------------------------------------------------------------------------
  const sessionCompletedRef = useRef(false)
  const [showTransitionDialog, setShowTransitionDialog] = useState(false)

  // ---------------------------------------------------------------------------
  // Time window tracking (DB persistence)
  // ---------------------------------------------------------------------------
  const [timeWindows, setTimeWindows] = useState<TimeWindow[]>([])
  const [currentWindowStart, setCurrentWindowStart] = useState<string | null>(
    null,
  )
  const timeWindowsRef = useRef<TimeWindow[]>([])
  const currentWindowStartRef = useRef<string | null>(null)
  const sessionIdRef = useRef<string | null>(sessionId)

  // Keep refs in sync
  useEffect(() => {
    timeWindowsRef.current = timeWindows
  }, [timeWindows])
  useEffect(() => {
    currentWindowStartRef.current = currentWindowStart
  }, [currentWindowStart])
  useEffect(() => {
    sessionIdRef.current = sessionId
  }, [sessionId])

  // tRPC mutations — use ref to avoid dependency instability.
  // useMutation() returns a new object each render; if saveTimerStateToDb
  // depended on it directly, every callback and effect downstream would
  // get new identities each render, causing infinite effect re-runs (OOM).
  const trpcUtils = trpc.useUtils()
  const saveTimerStateMutation = trpc.sessions.saveTimerState.useMutation({
    onSuccess: () => {
      // Matches main: only invalidate getHistory (for the Attempts tab).
      //
      // We deliberately do NOT update or invalidate the getCurrent cache
      // here. saveTimerStateToDb includes an open-window snapshot in its
      // payload, but the snapshot is purely a server-side checkpoint of
      // in-flight time — it must NOT leak into the client's
      // timeWindowsRef, because currentWindowStartRef is still tracking
      // that open window. A cache update would propagate through the
      // restoration effect into timeWindowsRef as a closed window with the
      // same startedAt as the open one, so the next save (which adds a
      // fresh snapshot of the same still-open window) would double-count
      // the overlapping interval. We let the cache stay stale during
      // active work; refresh resets it, and same-tab refetches pick up
      // fresh data via React Query's normal staleTime expiration.
      trpcUtils.sessions.getHistory.invalidate()
    },
  })
  const saveTimerStateMutationRef = useRef(saveTimerStateMutation)
  saveTimerStateMutationRef.current = saveTimerStateMutation

  // ---------------------------------------------------------------------------
  // Keep settingsRef and userSettingsRef in sync with useUserSettings
  // ---------------------------------------------------------------------------
  useEffect(() => {
    settingsRef.current = timerSettings
  }, [timerSettings])
  useEffect(() => {
    userSettingsRef.current = userSettings
  }, [userSettings])

  // ---------------------------------------------------------------------------
  // Preload alarm sound when it changes
  // ---------------------------------------------------------------------------
  useEffect(() => {
    alarmRef.current.preloadSound(timerSettings.alarmSound as AlarmSound)
  }, [timerSettings.alarmSound])

  // ---------------------------------------------------------------------------
  // Synced setters (update both ref and state)
  // ---------------------------------------------------------------------------
  const setActiveView = useCallback((view: 'stopwatch' | 'timer') => {
    activeViewRef.current = view
    setActiveViewState(view)
  }, [])

  const setHasTimer = useCallback((value: boolean) => {
    hasTimerRef.current = value
    setHasTimerState(value)
  }, [])

  // Sync React state from refs
  const syncState = useCallback(() => {
    const sw = stopwatchRef.current
    const tm = timerRef.current
    setStopwatch({ status: sw.status, elapsedMs: sw.elapsedMs })
    setTimer({
      status: tm.status,
      durationMs: tm.durationMs,
      remainingMs: tm.remainingMs,
    })
  }, [])

  // ---------------------------------------------------------------------------
  // Persistence
  // ---------------------------------------------------------------------------
  const persistNow = useCallback(() => {
    if (!slug) return
    const sw = stopwatchRef.current
    const tm = timerRef.current
    const state: PersistedTimerState = {
      stopwatch: {
        status: sw.status,
        elapsedMs: sw.elapsedMs,
        lastTickAt: sw.lastTickAt,
      },
      timer: {
        status: tm.status,
        durationMs: tm.durationMs,
        remainingMs: tm.remainingMs,
        lastTickAt: tm.lastTickAt,
      },
      activeView: activeViewRef.current,
      hasTimer: hasTimerRef.current,
      timeWindows: timeWindowsRef.current,
      currentWindowStart: currentWindowStartRef.current,
    }
    saveTimerState(slug, state)
  }, [slug])

  const throttledPersist = useCallback(() => {
    if (persistTimeoutRef.current) return // already scheduled
    if (sessionCompletedRef.current) return // don't re-create after session complete
    persistTimeoutRef.current = setTimeout(() => {
      persistNow()
      persistTimeoutRef.current = null
    }, PERSIST_DEBOUNCE_MS)
  }, [persistNow])

  // ---------------------------------------------------------------------------
  // Alarm helper
  // ---------------------------------------------------------------------------
  const initAlarm = useCallback(() => {
    alarmRef.current.initFromUserGesture()
  }, [])

  // ---------------------------------------------------------------------------
  // Instrument running helper
  // ---------------------------------------------------------------------------
  /** Returns true if at least one instrument (stopwatch or timer) is actively running. */
  const isAnyInstrumentRunning = useCallback((): boolean => {
    const sw = stopwatchRef.current
    const tm = timerRef.current
    return (
      sw.status === 'running' ||
      tm.status === 'running' ||
      tm.status === 'overage'
    )
  }, [])

  // ---------------------------------------------------------------------------
  // Time window helpers
  // ---------------------------------------------------------------------------
  const openTimeWindow = useCallback(() => {
    if (currentWindowStartRef.current) return // already open
    setCurrentWindowStart(new Date().toISOString())
  }, [])

  const closeTimeWindow = useCallback((): TimeWindow | null => {
    const start = currentWindowStartRef.current
    if (!start) return null
    const now = new Date().toISOString()
    const durationMs = new Date(now).getTime() - new Date(start).getTime()
    const window: TimeWindow = { startedAt: start, pausedAt: now, durationMs }
    // Update refs synchronously so saveTimerStateToDb can read immediately
    timeWindowsRef.current = [...timeWindowsRef.current, window]
    currentWindowStartRef.current = null
    setTimeWindows(timeWindowsRef.current)
    setCurrentWindowStart(null)
    return window
  }, [])

  // Clear all time window state (refs + React state) for session boundaries
  const clearTimeWindows = useCallback(() => {
    timeWindowsRef.current = []
    setTimeWindows([])
    currentWindowStartRef.current = null
    setCurrentWindowStart(null)
  }, [])

  const saveTimerStateToDb = useCallback(() => {
    const sid = sessionIdRef.current
    if (!sid) return
    const windows = [...timeWindowsRef.current]
    // If there's an open window, include a snapshot
    if (currentWindowStartRef.current) {
      const now = new Date().toISOString()
      const start = currentWindowStartRef.current
      const durationMs = new Date(now).getTime() - new Date(start).getTime()
      windows.push({ startedAt: start, pausedAt: now, durationMs })
    }
    // No "drop windows that predate session.startedAt" filter here on purpose:
    // when the user starts typing before saveDraft auto-creates a session,
    // the open time window legitimately starts earlier than session.startedAt
    // (createSession happens at the end of the 2s debounce). Filtering those
    // out lost the user's first seconds of work. Leakage from prior sessions
    // is prevented upstream by explicit clearTimeWindows() calls in every
    // session-transition handler (onSessionComplete, handleTransitionStartFresh,
    // resetForNewAttempt) and by the lifecycle reset effect on closed-* / stub
    // landings.
    const totalElapsedMs = windows.reduce((sum, w) => sum + w.durationMs, 0)
    saveTimerStateMutationRef.current.mutate({
      sessionId: sid,
      timeWindows: windows,
      totalElapsedMs,
    })
  }, [])

  // ---------------------------------------------------------------------------
  // Tick
  // ---------------------------------------------------------------------------
  const isRunning =
    stopwatch.status === 'running' ||
    timer.status === 'running' ||
    timer.status === 'overage'

  useEffect(() => {
    if (!isRunning) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
      return
    }

    intervalRef.current = setInterval(() => {
      const now = Date.now()
      const sw = stopwatchRef.current
      const tm = timerRef.current
      const currentSettings = settingsRef.current

      // Tick stopwatch
      if (sw.status === 'running' && sw.lastTickAt !== null) {
        const delta = now - sw.lastTickAt
        sw.elapsedMs += delta
      }
      if (sw.status === 'running') {
        sw.lastTickAt = now
      }

      // Tick timer
      if (
        (tm.status === 'running' || tm.status === 'overage') &&
        tm.lastTickAt !== null
      ) {
        const delta = now - tm.lastTickAt
        tm.remainingMs -= delta
      }
      if (tm.status === 'running' || tm.status === 'overage') {
        tm.lastTickAt = now
      }

      // Resolve alarm sound once — needed by both zero-crossing and threshold checks
      const alarmSound = (currentSettings.alarmSound ?? 'classic') as AlarmSound

      // Timer transition: running -> overage when remaining crosses 0
      if (tm.status === 'running' && tm.remainingMs <= 0) {
        // Fire zero alarm before status transition (otherwise the alarm check
        // won't see 'running' status when recordOverage is off)
        if (hasTimerRef.current) {
          alarmRef.current.checkAndFireAlarms(tm.remainingMs, [], alarmSound)
        }

        if (currentSettings.recordOverage) {
          tm.status = 'overage'
        } else {
          // Stop the timer only — stopwatch keeps running independently
          tm.status = 'paused'
          tm.remainingMs = 0
          tm.lastTickAt = null
        }
      }

      // Check super alarm thresholds (only while timer is actively counting)
      if (
        (tm.status === 'running' || tm.status === 'overage') &&
        currentSettings.superAlarmEnabled
      ) {
        alarmRef.current.checkAndFireAlarms(
          tm.remainingMs,
          currentSettings.superAlarmThresholds,
          alarmSound,
        )
      }
      // Fire zero alarm for overage mode (the running->overage case is handled above)
      if (
        tm.remainingMs <= 0 &&
        hasTimerRef.current &&
        tm.status === 'overage'
      ) {
        alarmRef.current.checkAndFireAlarms(tm.remainingMs, [], alarmSound)
      }

      syncState()
      throttledPersist()
    }, TICK_INTERVAL_MS)

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }
  }, [isRunning, syncState, throttledPersist])

  // ---------------------------------------------------------------------------
  // Actions
  // ---------------------------------------------------------------------------
  const startStopwatch = useCallback(() => {
    const sw = stopwatchRef.current
    if (sw.status === 'running') return

    const now = Date.now()
    sw.status = 'running'
    sw.lastTickAt = now

    // Always start timer too if it has remaining time (or is in overage)
    const tm = timerRef.current
    if (
      hasTimerRef.current &&
      (tm.status === 'paused' || tm.status === 'idle') &&
      tm.remainingMs !== 0
    ) {
      tm.status = tm.remainingMs < 0 ? 'overage' : 'running'
      tm.lastTickAt = now
      // Pre-mark alarms if resuming into overage so they don't re-fire
      if (tm.remainingMs < 0) {
        alarmRef.current.markAllFired(
          settingsRef.current.superAlarmThresholds ?? [],
        )
      }
    }

    openTimeWindow()
    initAlarm()
    syncState()
    throttledPersist()
  }, [openTimeWindow, initAlarm, syncState, throttledPersist])

  const pauseStopwatch = useCallback(() => {
    const sw = stopwatchRef.current
    if (sw.status !== 'running') return

    sw.status = 'paused'
    sw.lastTickAt = null

    // Always pause timer too
    const tm = timerRef.current
    if (tm.status === 'running' || tm.status === 'overage') {
      tm.status = 'paused'
      tm.lastTickAt = null
    }

    // Close time window only if no instrument remains running
    const shouldCloseWindow = !isAnyInstrumentRunning()
    if (shouldCloseWindow) {
      closeTimeWindow()
    }

    syncState()
    persistNow()
    if (shouldCloseWindow) saveTimerStateToDb()
  }, [
    isAnyInstrumentRunning,
    closeTimeWindow,
    syncState,
    persistNow,
    saveTimerStateToDb,
  ])

  const resetStopwatch = useCallback(() => {
    const sw = stopwatchRef.current
    sw.status = 'idle'
    sw.elapsedMs = 0
    sw.lastTickAt = null
    // Always reset timer too
    const tm = timerRef.current
    tm.status = 'idle'
    tm.remainingMs = tm.durationMs
    tm.lastTickAt = null
    alarmRef.current.stopAlarm()
    alarmRef.current.resetFiredThresholds()
    if (!isAnyInstrumentRunning()) {
      closeTimeWindow()
    }
    syncState()
    persistNow()
    if (sessionActiveRef.current) saveTimerStateToDb()
  }, [
    isAnyInstrumentRunning,
    closeTimeWindow,
    syncState,
    persistNow,
    saveTimerStateToDb,
  ])

  const startTimer = useCallback(
    (durationMs: number) => {
      const tm = timerRef.current

      if (tm.status === 'running') return

      const now = Date.now()

      if (tm.status === 'paused') {
        // Don't resume a fully expired timer (remainingMs === 0)
        // Negative remainingMs means overage — allow resume into overage state
        if (tm.remainingMs === 0) return
        // Resume: ignore durationMs, restore correct status
        tm.status = tm.remainingMs < 0 ? 'overage' : 'running'
        tm.lastTickAt = now
        // Pre-mark alarms if resuming into overage so they don't re-fire
        if (tm.remainingMs < 0) {
          alarmRef.current.markAllFired(
            settingsRef.current.superAlarmThresholds ?? [],
          )
        }
      } else {
        // Don't start with 0 duration
        if (durationMs <= 0) return
        // Start fresh (idle)
        tm.status = 'running'
        tm.durationMs = durationMs
        tm.remainingMs = durationMs
        tm.lastTickAt = now
        alarmRef.current.resetFiredThresholds()
      }

      setHasTimer(true)

      // Always start stopwatch too
      const sw = stopwatchRef.current
      if (sw.status !== 'running') {
        sw.status = 'running'
        sw.lastTickAt = now
      }

      openTimeWindow()
      initAlarm()
      syncState()
      throttledPersist()
    },
    [openTimeWindow, initAlarm, syncState, throttledPersist, setHasTimer],
  )

  const pauseTimer = useCallback(() => {
    const tm = timerRef.current
    if (tm.status !== 'running' && tm.status !== 'overage') return
    tm.status = 'paused'
    tm.lastTickAt = null

    // Always pause stopwatch too
    const sw = stopwatchRef.current
    if (sw.status === 'running') {
      sw.status = 'paused'
      sw.lastTickAt = null
    }

    closeTimeWindow()
    syncState()
    persistNow()
    saveTimerStateToDb()
  }, [closeTimeWindow, syncState, persistNow, saveTimerStateToDb])

  const resetTimer = useCallback(() => {
    const tm = timerRef.current
    tm.status = 'idle'
    tm.remainingMs = tm.durationMs
    tm.lastTickAt = null
    alarmRef.current.stopAlarm()
    alarmRef.current.resetFiredThresholds()
    // Always reset stopwatch too
    const sw = stopwatchRef.current
    sw.status = 'idle'
    sw.elapsedMs = 0
    sw.lastTickAt = null
    closeTimeWindow()
    syncState()
    persistNow()
    if (sessionActiveRef.current) saveTimerStateToDb()
  }, [closeTimeWindow, syncState, persistNow, saveTimerStateToDb])

  const endTimer = useCallback(() => {
    // Clear timer completely — return to programming view
    const tm = timerRef.current
    tm.status = 'idle'
    tm.durationMs = 0
    tm.remainingMs = 0
    tm.lastTickAt = null
    alarmRef.current.stopAlarm()
    alarmRef.current.resetFiredThresholds()
    setHasTimer(false)
    setActiveView('stopwatch')
    // Reset stopwatch to 00:00:00
    const sw = stopwatchRef.current
    sw.status = 'idle'
    sw.elapsedMs = 0
    sw.lastTickAt = null
    closeTimeWindow()
    syncState()
    persistNow()
    if (sessionActiveRef.current) saveTimerStateToDb()
  }, [
    closeTimeWindow,
    syncState,
    persistNow,
    saveTimerStateToDb,
    setHasTimer,
    setActiveView,
  ])

  const resetForNewAttempt = useCallback(() => {
    // Stopwatch → idle/0
    const sw = stopwatchRef.current
    sw.status = 'idle'
    sw.elapsedMs = 0
    sw.lastTickAt = null

    // Timer → idle. Preserve durationMs so the user's configured timer
    // sticks around; reset remainingMs to durationMs so a resume is sane.
    const tm = timerRef.current
    tm.status = 'idle'
    tm.lastTickAt = null
    tm.remainingMs = tm.durationMs

    alarmRef.current.stopAlarm()
    alarmRef.current.resetFiredThresholds()

    // Clear in-memory time windows and any pending throttled persist —
    // there's no current session that owns these, and the next attempt
    // will start a new window from zero.
    clearTimeWindows()
    if (persistTimeoutRef.current) {
      clearTimeout(persistTimeoutRef.current)
      persistTimeoutRef.current = null
    }

    // Clear localStorage so a page reload doesn't restore the expired
    // session's stopwatch (e.g. paused at ~24h) into the new attempt.
    if (slug) clearTimerState(slug)

    syncState()
  }, [clearTimeWindows, syncState, slug])

  const onSessionComplete = useCallback(() => {
    const currentSettings = settingsRef.current

    if (currentSettings.autoReset) {
      // Reset stopwatch
      const sw = stopwatchRef.current
      sw.status = 'idle'
      sw.elapsedMs = 0
      sw.lastTickAt = null

      // Reset timer based on resetTimerTo setting
      const tm = timerRef.current
      tm.status = 'idle'
      tm.lastTickAt = null
      if (currentSettings.resetTimerTo === 'zero') {
        tm.durationMs = 0
        tm.remainingMs = 0
        setHasTimer(false)
        setActiveView('stopwatch')
      } else {
        // 'lastDuration' — keep durationMs, reset remainingMs to it
        tm.remainingMs = tm.durationMs
      }

      alarmRef.current.stopAlarm()
      alarmRef.current.resetFiredThresholds()
    } else {
      // Auto-reset OFF — pause everything, keep values
      const sw = stopwatchRef.current
      if (sw.status === 'running') {
        sw.status = 'paused'
        sw.lastTickAt = null
      }

      const tm = timerRef.current
      if (tm.status === 'running' || tm.status === 'overage') {
        tm.status = 'paused'
        tm.lastTickAt = null
      }

      alarmRef.current.stopAlarm()
      // Mark that a session just completed — next interaction shows transition dialog
      sessionCompletedRef.current = true
    }

    closeTimeWindow()
    syncState()
    saveTimerStateToDb()

    // Cancel any pending throttled persist to prevent re-creation
    if (persistTimeoutRef.current) {
      clearTimeout(persistTimeoutRef.current)
      persistTimeoutRef.current = null
    }

    // Clear time windows so they don't leak into the next session
    clearTimeWindows()

    // Clear localStorage so stale windows aren't restored on next visit
    if (slug) clearTimerState(slug)
  }, [
    closeTimeWindow,
    syncState,
    saveTimerStateToDb,
    clearTimeWindows,
    slug,
    setHasTimer,
    setActiveView,
  ])

  // ---------------------------------------------------------------------------
  // Session transition callbacks
  // ---------------------------------------------------------------------------

  const handleNewSessionInteraction = useCallback((): boolean => {
    if (!sessionCompletedRef.current) return false

    const currentTimerSettings = settingsRef.current
    if (currentTimerSettings.autoReset) {
      sessionCompletedRef.current = false
      return false
    }

    const currentUserSettings = userSettingsRef.current
    if (!currentUserSettings.timer.showSessionTransitionNotice) {
      // Notice off — silently continue (don't reset, just mark done)
      sessionCompletedRef.current = false
      return false
    }

    // Show the transition dialog and block
    setShowTransitionDialog(true)
    return true
  }, [])

  const handleTransitionStartFresh = useCallback(() => {
    sessionCompletedRef.current = false
    setShowTransitionDialog(false)

    // Clear old session's time windows
    clearTimeWindows()

    // Reset stopwatch
    const sw = stopwatchRef.current
    sw.status = 'idle'
    sw.elapsedMs = 0
    sw.lastTickAt = null

    // Reset timer based on resetTimerTo setting
    const tm = timerRef.current
    tm.status = 'idle'
    tm.lastTickAt = null
    const resetTo = settingsRef.current.resetTimerTo
    if (resetTo === 'zero') {
      tm.durationMs = 0
      tm.remainingMs = 0
      setHasTimer(false)
      setActiveView('stopwatch')
    } else {
      tm.remainingMs = tm.durationMs
    }

    alarmRef.current.resetFiredThresholds()
    syncState()

    // Start the stopwatch for the new session
    startStopwatch()
  }, [syncState, setHasTimer, setActiveView, startStopwatch, clearTimeWindows])

  const handleTransitionContinue = useCallback(() => {
    sessionCompletedRef.current = false
    setShowTransitionDialog(false)
    // Clear old session's time windows (they're already saved to DB)
    clearTimeWindows()
    // Start stopwatch from current values
    startStopwatch()
  }, [startStopwatch, clearTimeWindows])

  const handleTransitionDismiss = useCallback(() => {
    setShowTransitionDialog(false)
    // sessionCompletedRef stays true — popup will reappear on next interaction
  }, [])

  // ---------------------------------------------------------------------------
  // Editor change → auto-start stopwatch
  // ---------------------------------------------------------------------------
  const onEditorChange = useCallback(
    (charsTyped: number) => {
      if (charsTyped <= 0) return
      const sw = stopwatchRef.current

      // Normal case: start from idle
      if (sw.status === 'idle') {
        startStopwatch()
        return
      }

      if (sw.status === 'paused') {
        // Session just completed (same mount cycle) — check transition dialog
        if (sessionCompletedRef.current) {
          const blocked = handleNewSessionInteraction()
          if (!blocked) {
            startStopwatch()
          }
          return
        }

        // Returned to a solved/no-session problem via navigation —
        // stopwatch restored as paused from localStorage, but no active session.
        // Reset and start fresh for the new attempt.
        // Skip while session query is still loading.
        if (!sessionActiveRef.current && !sessionLoadingRef.current) {
          sw.elapsedMs = 0
          sw.lastTickAt = null
          syncState()
        }
        // Typing is an active work signal — always resume.
        startStopwatch()
      }
    },
    [startStopwatch, handleNewSessionInteraction, syncState],
  )

  // ---------------------------------------------------------------------------
  // Load on mount / slug change
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!slug) {
      // Reset to defaults
      stopwatchRef.current = {
        status: 'idle',
        elapsedMs: 0,
        lastTickAt: null,
      }
      timerRef.current = {
        status: 'idle',
        durationMs: 0,
        remainingMs: 0,
        lastTickAt: null,
      }
      setHasTimer(false)
      setActiveView('stopwatch')
      syncState()
      return
    }

    const persisted = loadTimerState(slug)
    const reconstructed = reconstructTime(persisted)

    stopwatchRef.current = { ...reconstructed.stopwatch }
    timerRef.current = { ...reconstructed.timer }
    setHasTimer(reconstructed.hasTimer)
    setActiveView(reconstructed.activeView)

    // If timer is already past zero (overage), pre-mark all alarms as fired
    // so they don't re-fire on resume/reload
    if (reconstructed.timer.remainingMs < 0) {
      alarmRef.current.markAllFired(
        settingsRef.current.superAlarmThresholds ?? [],
      )
    }

    // Restore time windows from reconstructed state (stale windows already closed)
    setTimeWindows(reconstructed.timeWindows ?? [])
    setCurrentWindowStart(reconstructed.currentWindowStart ?? null)

    syncState()

    return () => {
      // Persist old problem's timer state to DB before loading new slug's state.
      // At cleanup time, refs still hold the outgoing problem's session ID and
      // time windows, so saveTimerStateToDb() captures the correct data.
      saveTimerStateToDb()
    }
  }, [slug, syncState, setHasTimer, setActiveView, saveTimerStateToDb])

  // ---------------------------------------------------------------------------
  // Lifecycle-aware reset: clear stopwatch on closed-* / stub landings.
  // ---------------------------------------------------------------------------
  // For closed-* (preload from a previous session) and *-stub landings, the
  // new attempt starts at 00:00. For open-* landings we deliberately leave the
  // stopwatch alone — localStorage (loaded by the mount effect above) carries
  // the user's actual accumulated time. Overriding it with a server-side
  // aggregate would revert to whatever the last `saveTimerStateToDb` call
  // persisted, which on flaky beforeunload paths can be older than what the
  // user just worked. This mirrors the invariant on `origin/main`: server
  // owns the time-window record for display in Attempts; the client's
  // localStorage owns the stopwatch's running tally for open sessions.
  // A ref guard ensures the reset fires exactly once per problem landing.
  const lifecycleResetAppliedForRef = useRef<string | null>(null)
  useEffect(() => {
    if (!slug || !landingMode) return // wait for slug and getCurrent to resolve

    const key = `${slug}:${landingMode}`
    if (lifecycleResetAppliedForRef.current === key) return
    lifecycleResetAppliedForRef.current = key

    switch (landingMode) {
      case 'closed-recent-silent':
      case 'closed-needs-decision':
      case 'fresh-stub':
      case 'closed-stale-stub':
      case 'open-expired-stub': {
        // Reset to 00:00:00; clear localStorage so a refresh doesn't restore
        // stale state; clear in-memory time windows so they don't leak into
        // whatever session the user starts next on this slug. Without the
        // in-memory clear, a user who worked on this problem previously could
        // come back to a closed-* / stub landing and accidentally have the
        // prior session's windows carry over into the next saveTimerStateToDb.
        stopwatchRef.current = {
          status: 'idle',
          elapsedMs: 0,
          lastTickAt: null,
        }
        timerRef.current = {
          ...timerRef.current,
          status: 'idle',
          remainingMs: timerRef.current.durationMs,
          lastTickAt: null,
        }
        clearTimerState(slug)
        clearTimeWindows()
        syncState()
        break
      }
      case 'open-silent':
      case 'open-needs-decision':
        // No-op. Stopwatch state already loaded from localStorage.
        break
      default: {
        // Exhaustiveness guard: adding a LandingMode makes `landingMode` fail
        // to narrow to `never` here, a compile error — forcing this effect to
        // decide which bucket the new mode belongs in. Mirrors the guard in
        // session-context.tsx (LG-391) over the same union.
        const exhaustive: never = landingMode
        throw new Error(`Unexpected LandingMode: ${exhaustive as string}`)
      }
    }
  }, [slug, landingMode, syncState, clearTimeWindows])

  // ---------------------------------------------------------------------------
  // Restore time windows from session whenever the server's timerState changes
  // ---------------------------------------------------------------------------
  // Mirror main's invariant: server-side timerState is the source of truth.
  // Re-sync local timeWindows on every timerState identity change (every
  // getCurrent refetch) so the freshest server data overrides any stale
  // local state. Update the ref synchronously alongside the state setter so
  // a near-immediate saveTimerStateToDb sees the correct windows without
  // waiting on the [timeWindows] sync effect at line 191-193.
  //
  // Previously this was keyed on sessionId, which caused a "first render
  // wins" race: on a fresh mount, sessionCtx.timerState was undefined while
  // the query resolved, the effect set windows=[], stamped
  // restoredForSessionRef = sessionId, and then ignored the real data when
  // it arrived. Subsequent saveTimerStateToDb calls then wrote the empty
  // list back to the server, destroying recorded time windows.
  useEffect(() => {
    if (!sessionCtx?.timerState) return
    const windows = sessionCtx.timerState.timeWindows ?? []
    timeWindowsRef.current = windows
    setTimeWindows(windows)
  }, [sessionCtx?.timerState])

  // ---------------------------------------------------------------------------
  // beforeunload + visibilitychange
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const pauseAndPersist = () => {
      // Don't persist if no session exists yet
      if (!sessionActiveRef.current) return
      // Don't re-create state that onSessionComplete just cleared
      if (sessionCompletedRef.current) return

      // Close any open time window into refs before persisting
      const start = currentWindowStartRef.current
      if (start) {
        const now = new Date().toISOString()
        const durationMs = new Date(now).getTime() - new Date(start).getTime()
        timeWindowsRef.current = [
          ...timeWindowsRef.current,
          { startedAt: start, pausedAt: now, durationMs },
        ]
        currentWindowStartRef.current = null
      }

      // Pause stopwatch/timer in refs so persisted state is clean
      const sw = stopwatchRef.current
      if (sw.status === 'running') {
        sw.status = 'paused'
        sw.lastTickAt = null
      }
      const tm = timerRef.current
      if (tm.status === 'running' || tm.status === 'overage') {
        tm.status = 'paused'
        tm.lastTickAt = null
      }

      persistNow()
      saveTimerStateToDb()
    }

    const handleBeforeUnload = () => pauseAndPersist()
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') pauseAndPersist()
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [persistNow, saveTimerStateToDb])

  // ---------------------------------------------------------------------------
  // Cleanup debounce timeout on unmount
  // ---------------------------------------------------------------------------
  useEffect(() => {
    return () => {
      if (persistTimeoutRef.current) {
        clearTimeout(persistTimeoutRef.current)
      }
    }
  }, [])

  // ---------------------------------------------------------------------------
  // One-time migration: localStorage timer settings → database
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!isSignedIn) return

    const LEGACY_KEY = 'bctci-timer-settings'
    const raw = localStorage.getItem(LEGACY_KEY)
    if (!raw) return

    try {
      const legacy = JSON.parse(raw)
      // Map legacy nested shape to new flat shape
      updateUserSettings({
        timer: {
          alarmSound: legacy.alarmSound ?? 'classic',
          autoReset: legacy.autoReset ?? false,
          superAlarmEnabled: legacy.superAlarm?.enabled ?? false,
          superAlarmThresholds: legacy.superAlarm?.thresholds ?? [],
          recordOverage: legacy.recordOverage ?? true,
        },
      })
      localStorage.removeItem(LEGACY_KEY)
    } catch {
      localStorage.removeItem(LEGACY_KEY)
    }
  }, [isSignedIn, updateUserSettings])

  // ---------------------------------------------------------------------------
  // Context value
  // ---------------------------------------------------------------------------
  const value = useMemo<TimerContextValue>(
    () => ({
      activeView,
      stopwatch,
      timer,
      hasTimer,
      startStopwatch,
      pauseStopwatch,
      resetStopwatch,
      startTimer,
      pauseTimer,
      resetTimer,
      endTimer,
      setActiveView,
      settings: timerSettings,
      onEditorChange,
      saveTimerStateToDb,
      onSessionComplete,
      showTransitionDialog,
      handleNewSessionInteraction,
      handleTransitionStartFresh,
      handleTransitionContinue,
      handleTransitionDismiss,
      resetForNewAttempt,
    }),
    [
      activeView,
      stopwatch,
      timer,
      hasTimer,
      startStopwatch,
      pauseStopwatch,
      resetStopwatch,
      startTimer,
      pauseTimer,
      resetTimer,
      endTimer,
      setActiveView,
      timerSettings,
      onEditorChange,
      saveTimerStateToDb,
      onSessionComplete,
      showTransitionDialog,
      handleNewSessionInteraction,
      handleTransitionStartFresh,
      handleTransitionContinue,
      handleTransitionDismiss,
      resetForNewAttempt,
    ],
  )

  return (
    <TimerContext value={value}>
      {children}
      <SessionTransitionDialog
        open={showTransitionDialog}
        onStartFresh={handleTransitionStartFresh}
        onContinue={handleTransitionContinue}
        onDismiss={handleTransitionDismiss}
        stopwatchMs={stopwatchRef.current.elapsedMs}
        timerMs={timerRef.current.remainingMs}
        timerDurationMs={timerRef.current.durationMs}
        hasTimer={hasTimerRef.current}
        recordOverage={timerSettings.recordOverage}
      />
    </TimerContext>
  )
}

// =============================================================================
// Hooks
// =============================================================================

export function useTimer(): TimerContextValue {
  const context = use(TimerContext)
  if (!context) {
    throw new Error('useTimer must be used within a TimerProvider')
  }
  return context
}

export function useTimerOptional(): TimerContextValue | null {
  return use(TimerContext)
}
